import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  HourglassEmpty as HourglassEmptyIcon,
} from '@mui/icons-material';
import {
  Badge,
  Box,
  Chip,
  Collapse,
  Container,
  LinearProgress,
  List,
  ListItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { BackgroundJob } from 'BackgroundJob';
import { useJobs } from 'JobContext';
import React, { useCallback, useState } from 'react';
import { Palette } from 'Palette';
import { StyledIconButton } from '../styles/StyledButtons';
import { StyledDocumentLink } from '../styles/StyledLink';
import { StyledTypography } from '../styles/styles';

const JobsPage: React.FC = () => {
  const { jobs, loading, dismissJob, navigateToJob, jobResultLink } = useJobs();
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());

  const handleDismissJob = useCallback(
    (job: BackgroundJob, event: React.MouseEvent) => {
      event.stopPropagation();
      dismissJob(job);
    },
    [dismissJob],
  );

  const handleJobClick = useCallback(
    (job: BackgroundJob) => {
      if (job.linkKey && job.linkedModelId) {
        navigateToJob(job);
      }
    },
    [navigateToJob],
  );

  const handleExpandToggle = useCallback((jobId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedJobs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  }, []);

  const getJobIcon = (status: BackgroundJob['status']) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircleIcon sx={{ color: 'green' }} />;
      case 'FAILED':
        return <ErrorIcon sx={{ color: 'red' }} />;
      case 'IN_PROGRESS':
        return <HourglassEmptyIcon sx={{ color: 'blue' }} />;
      default:
        return <HourglassEmptyIcon color="disabled" />;
    }
  };

  const getJobStatusColor = (status: BackgroundJob['status']) => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'FAILED':
        return 'error';
      case 'IN_PROGRESS':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getBadgeColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'in progress':
        return 'primary'; // blue
      case 'failed':
        return 'error'; // red
      case 'recently completed':
        return 'success'; // green
      default:
        return 'default';
    }
  };

  const getChildJobStatusCounts = (childJobs: BackgroundJob[]) => {
    const counts = {
      COMPLETED: 0,
      FAILED: 0,
      IN_PROGRESS: 0,
      PENDING: 0,
    };

    childJobs.forEach((job) => {
      counts[job.status]++;
    });

    return counts;
  };

  const renderChildJob = (job: BackgroundJob, isChild: boolean = false) => {
    const isExpanded = expandedJobs.has(job.id);
    const hasChildJobs = job.childJobs && job.childJobs.length > 0;
    const childStatusCounts =
      hasChildJobs && job.childJobs ? getChildJobStatusCounts(job.childJobs) : null;

    return (
      <ListItem
        key={job.id}
        onClick={() => handleJobClick(job)}
        sx={{
          border: `1px solid ${Palette.tone2.border}`,
          borderRadius: 1,
          mb: 1,
          cursor: job.linkKey ? 'pointer' : 'default',
          '&:hover': job.linkKey ? { backgroundColor: Palette.tone2.background } : {},
          display: 'flex',
          flexDirection: 'column',
          ml: isChild ? 3 : 0, // Indent child jobs
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {getJobIcon(job.status)}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              ml: 1,
              flexGrow: 1,
            }}
          >
            <StyledTypography>{job.title}</StyledTypography>
            <Box>
              {job.progressText && (
                <StyledTypography fontSize="0.875rem">{job.progressText}</StyledTypography>
              )}

              {job.status === 'COMPLETED' && jobResultLink(job) && (
                <StyledDocumentLink
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigateToJob(job);
                    return false;
                  }}
                >
                  View Result
                </StyledDocumentLink>
              )}

              {(job.status === 'PENDING' || job.status === 'IN_PROGRESS') &&
                new Date(job.updatedAt ?? 0).getTime() < Date.now() - 12 * 60 * 60 * 1000 && (
                  <Typography color="warning.main" variant="caption" sx={{ ml: 1 }}>
                    This job looks stale and probably failed.
                  </Typography>
                )}
              {job.status === 'FAILED' && job.progressText && (
                <StyledTypography variant="caption" color="error">
                  {job.progressText}
                </StyledTypography>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {/* Child job status chips for parent jobs */}
            {hasChildJobs && childStatusCounts && (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {childStatusCounts.COMPLETED > 0 && (
                  <Chip
                    label={`${childStatusCounts.COMPLETED} ✓`}
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                )}
                {childStatusCounts.IN_PROGRESS > 0 && (
                  <Chip
                    label={`${childStatusCounts.IN_PROGRESS} ⏳`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                )}
                {childStatusCounts.FAILED > 0 && (
                  <Chip
                    label={`${childStatusCounts.FAILED} ✗`}
                    size="small"
                    color="error"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                )}
                {childStatusCounts.PENDING > 0 && (
                  <Chip
                    label={`${childStatusCounts.PENDING} ⏸`}
                    size="small"
                    color="default"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                )}
              </Box>
            )}

            {/* Expand/collapse button for parent jobs */}
            {hasChildJobs && (
              <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
                <StyledIconButton size="small" onClick={(e) => handleExpandToggle(job.id, e)}>
                  {isExpanded ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </StyledIconButton>
              </Tooltip>
            )}

            <Chip
              label={job.status}
              size="small"
              color={getJobStatusColor(job.status) as any}
              variant="outlined"
            />
            {(job.status === 'FAILED' ||
              job.status === 'COMPLETED' ||
              new Date(job.updatedAt ?? 0).getTime() < Date.now() - 12 * 60 * 60 * 1000) && (
              <Tooltip title="Dismiss">
                <StyledIconButton size="small" onClick={(e) => handleDismissJob(job, e)}>
                  <CloseIcon fontSize="small" />
                </StyledIconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {job.status === 'IN_PROGRESS' && (
          <LinearProgress
            variant="indeterminate"
            sx={{ width: '100%', height: 4, borderRadius: 2, mt: 1 }}
          />
        )}

        {/* Child jobs */}
        {hasChildJobs && job.childJobs && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 2, width: '100%' }}>
              <StyledTypography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Child Jobs ({job.childJobs.length})
              </StyledTypography>
              {job.childJobs.map((childJob) => renderChildJob(childJob, true))}
            </Box>
          </Collapse>
        )}
      </ListItem>
    );
  };

  const renderJobSection = (title: string, jobs: BackgroundJob[]) => {
    if (jobs.length === 0) return null;

    return (
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <StyledTypography variant="h5">{title}</StyledTypography>
          <Badge badgeContent={jobs.length} color={getBadgeColor(title) as any} sx={{ ml: 2 }} />
        </Box>
        <List dense>{jobs.map((job) => renderChildJob(job))}</List>
      </Box>
    );
  };

  if (loading || !jobs) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <HourglassEmptyIcon />
          <StyledTypography sx={{ ml: 1 }}>Loading jobs...</StyledTypography>
        </Box>
      </Container>
    );
  }

  const { runningJobs = [], failedJobs = [], recentlyCompletedJobs = [] } = jobs;

  const hasJobs =
    runningJobs.length > 0 || failedJobs.length > 0 || recentlyCompletedJobs.length > 0;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <StyledTypography variant="h4" sx={{ mb: 4 }}>
        Job Status
      </StyledTypography>

      {!hasJobs ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <StyledTypography>No active jobs to display.</StyledTypography>
        </Box>
      ) : (
        <Box>
          {renderJobSection('In Progress', runningJobs)}
          {renderJobSection('Failed', failedJobs)}
          {renderJobSection('Recently Completed', recentlyCompletedJobs)}
        </Box>
      )}
    </Container>
  );
};

export default JobsPage;
